import { useEffect, useState } from "react";
import { fetchAdminSettings, adminUpdateSettings, fetchContent } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Type, Save, Loader2, Pin } from "lucide-react";
import { toast } from "sonner";

const inp = "w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none";

const FIELD_LABEL = {
  title_deva: "Devanagari title",
  title_en: "English title / overline",
  sub_en: "Subtitle (English)",
  sub_deva: "Subtitle (Hindi)",
  overline: "Overline (small label)",
  desc_en: "Explainer / subheading (English)",
  desc_deva: "Explainer / subheading (Hindi)",
  en: "English",
  hi: "Hindi",
};
const LONG = new Set(["sub_en", "sub_deva", "desc_en", "desc_deva"]);
const SECTION_FIELDS = ["title_deva", "title_en", "sub_en", "sub_deva"];
const HOME_FIELDS = ["overline", "title_en", "title_deva", "desc_en", "desc_deva"];
const LABEL_FIELDS = ["en", "hi"];

// Reusable UI labels on content/hub detail pages (keys read via settings.copy()).
const DETAIL_LABELS = [
  { key: "detail.on_this_page", label: "'On this page' (table of contents)", fields: LABEL_FIELDS, d: { en: "On this page", hi: "इस पृष्ठ पर" } },
  { key: "detail.transliteration", label: "Transliteration heading", fields: LABEL_FIELDS, d: { en: "Transliteration (IAST)", hi: "लिप्यंतरण (IAST)" } },
  { key: "detail.muhurta", label: "Auspicious Muhurta heading", fields: LABEL_FIELDS, d: { en: "Auspicious Muhurta", hi: "शुभ मुहूर्त" } },
  { key: "detail.listen", label: "'Listen' (audio) heading", fields: LABEL_FIELDS, d: { en: "Listen", hi: "श्रवण" } },
];

// Homepage section headings (keys are "home.<slot>", read by Home.jsx).
const HOME = [
  { key: "home.explore", label: "Explore Dharmartha", fields: HOME_FIELDS, d: { overline: "Explore", title_en: "Explore Dharmartha", desc_en: "Six gateways into the knowledge traditions of Bharat." } },
  { key: "home.spotlight", label: "Daily Spotlight", fields: HOME_FIELDS, d: { overline: "Today", title_en: "Today's Reading", desc_en: "A hand-picked text to read today." } },
  { key: "home.temples", label: "Temples", fields: HOME_FIELDS, d: { overline: "Temples", title_en: "Temples of Bharat" } },
  { key: "home.featured", label: "Featured Knowledge", fields: HOME_FIELDS, d: { overline: "Featured", title_en: "Featured Knowledge" } },
  { key: "home.services", label: "Services & Jyotisha", fields: HOME_FIELDS, d: { overline: "Services", title_en: "Services & Jyotisha", desc_en: "Bookable pujas, homams and consultations." } },
  { key: "home.glossary", label: "Glossary", fields: HOME_FIELDS, d: { overline: "Glossary", title_en: "Explore the Glossary", title_deva: "शब्दकोश", desc_en: "Key terms of the tradition, explained." } },
  { key: "home.recent", label: "Recently Added", fields: HOME_FIELDS, d: { overline: "New", title_en: "Recently Added" } },
];

// Section landing pages (hero title + subtitle).
const SECTIONS = [
  { key: "kathas", label: "Kathas", fields: SECTION_FIELDS, d: { title_deva: "कथा", title_en: "Kathas", sub_en: "Stories and narratives from India's spiritual traditions." } },
  { key: "mantras", label: "Mantras", fields: SECTION_FIELDS, d: { title_deva: "मंत्र", title_en: "Mantras", sub_en: "Mantras with Devanagari, transliteration, meaning and context." } },
  { key: "suktas", label: "Suktas", fields: SECTION_FIELDS, d: { title_deva: "सूक्त", title_en: "Suktas", sub_en: "Vedic hymns with Devanagari, transliteration and meaning." } },
  { key: "stotras", label: "Stotras", fields: SECTION_FIELDS, d: { title_deva: "स्तोत्र", title_en: "Stotras", sub_en: "Devotional hymns to the deities." } },
  { key: "rituals", label: "Rituals & Pooja Guides", fields: SECTION_FIELDS, d: { title_deva: "पूजा विधि", title_en: "Rituals & Pooja Guides", sub_en: "Step-by-step guides for commonly performed poojas." } },
  { key: "festivals", label: "Festivals", fields: SECTION_FIELDS, d: { title_deva: "उत्सव", title_en: "Festivals", sub_en: "Hindu festivals, vrats and observances." } },
  { key: "granthas", label: "Sacred Scriptures", fields: SECTION_FIELDS, d: { title_deva: "ग्रंथ", title_en: "Sacred Scriptures", sub_en: "Chapter-based sacred texts." } },
  { key: "philosophy", label: "Dharma & Philosophy (Hub)", fields: SECTION_FIELDS, d: { title_deva: "दर्शन", title_en: "Philosophy", sub_en: "Foundational concepts of Sanatana thought." } },
  { key: "scriptures", label: "Scriptures (Hub)", fields: SECTION_FIELDS, d: { title_deva: "शास्त्र", title_en: "Scriptures", sub_en: "The corpus of sacred texts." } },
  { key: "rishis", label: "Rishis (Hub)", fields: SECTION_FIELDS, d: { title_deva: "ऋषि", title_en: "Rishis", sub_en: "The seers of the tradition." } },
  { key: "pilgrimage", label: "Pilgrimage (Hub)", fields: SECTION_FIELDS, d: { title_deva: "तीर्थ", title_en: "Pilgrimage", sub_en: "Sacred sites and pilgrimage traditions." } },
  { key: "samskaras", label: "Samskaras (Hub)", fields: SECTION_FIELDS, d: { title_deva: "संस्कार", title_en: "Samskaras", sub_en: "The sacraments of life." } },
];

// New pages & reusable form/CTA copy (admin-editable + translatable).
const NEW_PAGES = [
  { key: "feedback.hero", label: "Feedback page hero", fields: HOME_FIELDS, d: { overline: "Help us improve", title_en: "Feedback & Bug Report", title_deva: "प्रतिक्रिया", desc_en: "Found a bug or have a content suggestion? Tell us — we're listening." } },
  { key: "collaborate.hero", label: "Collaborate page hero", fields: HOME_FIELDS, d: { overline: "Join the mission", title_en: "Collaborate & Work With Us", title_deva: "सहयोग करें", desc_en: "Dharmartha is a community effort to preserve and share the knowledge traditions of Bharat." } },
  { key: "collaborate.intro", label: "Collaborate page intro paragraph", fields: LABEL_FIELDS, d: { en: "Whether you are a scholar, a devotee, a translator, a developer or a temple — your contribution helps keep this knowledge authentic, living and freely available.", hi: "आप विद्वान हों, भक्त हों, अनुवादक, डेवलपर या मंदिर — आपका योगदान इस ज्ञान को प्रामाणिक, जीवंत एवं निःशुल्क बनाए रखने में सहायक है।" } },
  { key: "report.title", label: "Content page — 'Report an issue' heading", fields: LABEL_FIELDS, d: { en: "Spot an error or have a suggestion?", hi: "कोई त्रुटि दिखी या सुझाव है?" } },
  { key: "report.desc", label: "Content page — 'Report an issue' subtext", fields: LABEL_FIELDS, d: { en: "Help us keep this accurate — report a mistake or suggest content for this page.", hi: "इसे सटीक बनाए रखने में सहायता करें — त्रुटि बताएँ या सामग्री सुझाएँ।" } },
  { key: "report.cta", label: "Content page — 'Report an issue' button", fields: LABEL_FIELDS, d: { en: "Report an issue / Suggest a change", hi: "समस्या बताएँ / सुझाव दें" } },
];

const GROUPS = [
  { title: "Homepage sections", note: "Headings shown on the homepage.", pages: HOME },
  { title: "Section & Hub pages", note: "Hero title + subtitle at the top of each listing page.", pages: SECTIONS },
  { title: "Detail page labels", note: "Reusable labels shown on every content/hub detail page.", pages: DETAIL_LABELS },
  { title: "Feedback, Collaborate & 'Report an issue'", note: "Copy for the Feedback & Collaborate pages and the report-an-issue block shown on every content page. Editable in English + Hindi.", pages: NEW_PAGES },
];

export default function AdminPageHeadings() {
  const { reload } = useSettings();
  const [headings, setHeadings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState([]);
  const [pinFilter, setPinFilter] = useState("");

  useEffect(() => { fetchAdminSettings().then((s) => setHeadings(s.page_headings || {})).catch(() => setHeadings({})); }, []);
  useEffect(() => { fetchContent({ limit: 300 }).then(setContent).catch(() => {}); }, []);

  const set = (key, field, value) => setHeadings((h) => ({ ...h, [key]: { ...(h[key] || {}), [field]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      const clean = {};
      Object.entries(headings).forEach(([k, v]) => {
        const o = {};
        Object.entries(v || {}).forEach(([f, val]) => { if (typeof val === "string" && val.trim()) o[f] = val.trim(); });
        if (Object.keys(o).length) clean[k] = o;
      });
      await adminUpdateSettings({ page_headings: clean });
      setHeadings(clean);
      reload();
      toast("Page copy saved", { description: "Republish (Sync) to update the live site." });
    } catch (e) { toast.error("Save failed", { description: e.response?.data?.detail || e.message }); }
    finally { setSaving(false); }
  };

  if (headings === null) return <p className="py-20 text-center text-ink-3">…</p>;

  return (
    <div className="mx-auto max-w-4xl" data-testid="admin-page-headings">
      <div className="mb-2 flex items-center gap-2.5">
        <Type className="h-6 w-6 text-saffron" />
        <h1 className="font-serif-latin text-3xl text-ink">Page Copy — Headings & Explainers</h1>
      </div>
      <p className="mb-8 max-w-2xl text-sm text-ink-3">
        Edit the headings, subheadings and explainer text across the homepage and the section/hub pages.
        Leave a field blank to keep the built-in default.
      </p>

      {GROUPS.map((g) => (
        <div key={g.title} className="mb-10">
          <h2 className="mb-1 font-serif-latin text-xl text-ink">{g.title}</h2>
          <p className="mb-4 text-xs text-ink-3">{g.note}</p>
          <div className="space-y-5">
            {g.pages.map((p) => {
              const v = headings[p.key] || {};
              const slug = p.key.replace(/\./g, "-");
              return (
                <div key={p.key} className="border border-line bg-surface p-4" data-testid={`heading-${slug}`}>
                  <p className="mb-3 text-sm font-medium text-ink">{p.label} <span className="text-ink-3">· {p.key}</span></p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {p.fields.map((f) => (
                      <div key={f} className={LONG.has(f) ? "sm:col-span-2" : ""}>
                        <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">{FIELD_LABEL[f] || f}</label>
                        {LONG.has(f) ? (
                          <textarea value={v[f] || ""} onChange={(e) => set(p.key, f, e.target.value)} rows={2} placeholder={p.d[f] || ""} className={inp} data-testid={`heading-${slug}-${f}`} />
                        ) : (
                          <input value={v[f] || ""} onChange={(e) => set(p.key, f, e.target.value)} placeholder={p.d[f] || ""} className={inp} data-testid={`heading-${slug}-${f}`} />
                        )}
                      </div>
                    ))}
                  </div>
                  {p.key === "home.spotlight" && (
                    <div className="mt-4 rounded-sm border border-gold/40 bg-parchment/60 p-3" data-testid="spotlight-pin">
                      <label className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ink-2"><Pin className="h-3.5 w-3.5 text-saffron" /> "In focus today" — pinned article</label>
                      <p className="mb-2 text-xs text-ink-3">Pin a specific published article as the daily spotlight. Leave on "Auto-rotate daily" to let it change automatically.</p>
                      <input value={pinFilter} onChange={(e) => setPinFilter(e.target.value)} placeholder="Search articles by title / slug…" className={`${inp} mb-2`} data-testid="spotlight-pin-search" />
                      <select value={v.pinned_slug || ""} onChange={(e) => set(p.key, "pinned_slug", e.target.value)} className={inp} data-testid="spotlight-pin-select">
                        <option value="">Auto-rotate daily (default)</option>
                        {v.pinned_slug && !content.some((c) => c.slug === v.pinned_slug) && <option value={v.pinned_slug}>{v.pinned_slug} (current)</option>}
                        {content
                          .filter((c) => { const q = pinFilter.trim().toLowerCase(); return !q || `${c.title || ""} ${c.slug} ${c.type || ""}`.toLowerCase().includes(q); })
                          .slice(0, 200)
                          .map((c) => <option key={c.slug} value={c.slug}>{(c.title || c.slug)} · {c.type}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <div className="sticky bottom-4 mt-6">
        <button onClick={save} disabled={saving} data-testid="headings-save" className="inline-flex items-center gap-2 bg-[var(--saffron)] px-7 py-3 text-sm font-medium text-white shadow-lg transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save copy
        </button>
      </div>
    </div>
  );
}
